import { buildSoccerDietSlugAliases } from './sharedMeals';

export const GENDERS = [
  { key: 'women', label: 'Women' },
  { key: 'men', label: 'Men' },
];

export const WELLNESS_HUB_SLUG = 'ohio-indian-health-wellness-guide';

export const AGE_GROUPS = [
  { key: '18-34', label: '18–34', shortLabel: '18–34' },
  { key: '35-49', label: '35–49', shortLabel: '35–49' },
  { key: '50-plus', label: '50+', shortLabel: '50+' },
];

/** @param {'men'|'women'} genderKey @param {string} ageKey */
export function getAudienceLabel(genderKey, ageKey) {
  const gender = GENDERS.find((g) => g.key === genderKey)?.label ?? 'Adults';
  const age = AGE_GROUPS.find((a) => a.key === ageKey)?.label ?? '';
  return `${gender} ${age}`.trim();
}

/** Segment-specific copy for Ohio Indian wellness (diet + exercise) */
export function getSegmentProfile(genderKey, ageKey) {
  const audience = getAudienceLabel(genderKey, ageKey);
  const isWomen = genderKey === 'women';
  const isYoung = ageKey === '18-34';
  const isMid = ageKey === '35-49';
  const isOlder = ageKey === '50-plus';

  let proteinRange = '1.2–1.6 g/kg';
  let portionNote =
    'Portions suit many adults roughly 55–85 kg—adjust for your weight, activity, and appetite.';
  let warmupMinutes = 10;
  let hiitRecommended = isYoung;
  let jointEmphasis = isMid || isOlder;
  let strengthDays = 2;

  if (isYoung) {
    proteinRange = isWomen ? '1.2–1.6 g/kg' : '1.4–1.8 g/kg';
    warmupMinutes = 10;
  } else if (isMid) {
    proteinRange = isWomen ? '1.4–1.7 g/kg' : '1.4–1.8 g/kg';
    warmupMinutes = 12;
    portionNote =
      'Portions suit many adults around 60–85 kg—favor steady energy over aggressive crash diets.';
  } else {
    proteinRange = isWomen ? '1.4–1.8 g/kg' : '1.4–1.8 g/kg';
    warmupMinutes = 15;
    hiitRecommended = false;
    strengthDays = 2;
    portionNote =
      'Portions suit many adults 60–90 kg—prioritize protein, fiber, and hydration; lean out slowly with your clinician if needed.';
  }

  const dietWhoLead = isWomen
    ? `You are a woman (${AGE_GROUPS.find((a) => a.key === ageKey)?.label}) who wants balanced, Indian-style meals while living in Ohio.`
    : `You are a man (${AGE_GROUPS.find((a) => a.key === ageKey)?.label}) who wants balanced, Indian-style meals while living in Ohio.`;

  const nutritionPriorities = [];

  if (isWomen) {
    nutritionPriorities.push(
      'Iron and protein: pair dal, spinach, and eggs (if you eat them) with citrus; separate tea from iron-rich meals when possible.',
      'Calcium and vitamin D for bone health—yogurt, milk, paneer, and winter D3 per your doctor.',
      'Eat enough across the day—skipping meals often leads to evening overeating.'
    );
    if (isYoung) {
      nutritionPriorities.push(
        'If you are pregnant, breastfeeding, or trying to conceive, do not follow this plan without your OB or dietitian—needs change completely.'
      );
    }
    if (isMid) {
      nutritionPriorities.push(
        'Perimenopause can shift sleep and weight—consistent protein, fiber, and movement help; hormones are a clinician conversation.'
      );
    }
    if (isOlder) {
      nutritionPriorities.push(
        'Bone and muscle: protein at every meal, strength work if you are cleared to exercise, and steady hydration year-round.'
      );
    }
  } else {
    nutritionPriorities.push(
      'Protein spread across meals (eggs, dal, paneer, fish/chicken if non-veg) to support muscle and fullness.',
      'Fiber-rich carbs and vegetables for heart and blood sugar health.',
      'Moderate alcohol—it adds empty calories and can disrupt sleep.'
    );
    if (isMid || isOlder) {
      nutritionPriorities.push(
        'Watch sodium and saturated fat from restaurant curries if blood pressure or cholesterol are concerns—annual labs with your doctor.'
      );
    }
    if (isOlder) {
      nutritionPriorities.push(
        'Favor grilled, baked, or sautéed dishes over deep-fried snacks for easier digestion and heart health.'
      );
    }
  }

  if (isYoung) {
    nutritionPriorities.push(
      'Build habits you can keep—regular meal times beat extreme rules you cannot sustain.'
    );
  }

  nutritionPriorities.push(
    'Use local Indian grocers and supermarkets in Ohio for staples and fresh produce.'
  );

  const exerciseFocus = isWomen
    ? 'Strength, core, and cardio'
    : 'Strength, core, and conditioning';

  const exerciseTitle = isWomen
    ? `Fitness & Core Plan for ${audience} (Ohio)`
    : isYoung
      ? `Soccer Training & Core Plan for ${audience} (Ohio)`
      : `Soccer Strength & Core Plan for ${audience} (Ohio)`;

  return {
    audience,
    dietWhoLead,
    nutritionPriorities,
    proteinRange,
    portionNote,
    warmupMinutes,
    hiitRecommended,
    jointEmphasis,
    strengthDays,
    exerciseFocus,
    exerciseTitle,
  };
}

export function dietSlug(dietType, genderKey, ageKey) {
  const prefix = dietType === 'vegetarian' ? 'vegetarian-healthy-diet' : 'non-vegetarian-healthy-diet';
  return `${prefix}-${genderKey}-${ageKey}-ohio-indian`;
}

export function trainingSlug(genderKey, ageKey) {
  if (genderKey === 'women') {
    return `fitness-training-plan-women-${ageKey}-ohio-indian`;
  }
  return `soccer-training-plan-${genderKey}-${ageKey}-ohio-indian`;
}

function buildWomenTrainingSlugAliases() {
  const aliases = {};
  AGE_GROUPS.forEach((age) => {
    aliases[`soccer-training-plan-women-${age.key}-ohio-indian`] = trainingSlug('women', age.key);
  });
  return aliases;
}

export const LEGACY_SLUG_ALIASES = {
  ...buildSoccerDietSlugAliases(dietSlug),
  ...buildWomenTrainingSlugAliases(),
  'ohio-indian-soccer-wellness-guide': WELLNESS_HUB_SLUG,
  'six-pack-training-plan-soccer-players': trainingSlug('men', '35-49'),
};
